import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePortalContext } from '../../contexts/PortalContext';
import { Home, FileText, Briefcase, Plus, LogOut, Menu, X } from 'lucide-react';

const PortalLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customer, business, logout } = usePortalContext();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleNavClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  // Get branding - use business branding if available, otherwise defaults
  const brandColor = business?.brandColor || '#3b82f6';
  const businessName = business?.name || 'Customer Portal';
  const logoUrl = business?.logoUrl || null;
  const businessPhone = business?.phone || null;
  const businessEmail = business?.email || null;

  const navItems = [
    { path: '/portal/dashboard', icon: Home, label: 'Dashboard' },
    { path: '/portal/jobs', icon: Briefcase, label: 'My Jobs' },
    { path: '/portal/invoices', icon: FileText, label: 'Invoices' },
    { path: '/portal/request-quote', icon: Plus, label: 'Request Quote' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white sticky top-0 z-20">
        {/* Brand color accent bar */}
        <div style={{ backgroundColor: brandColor, height: '4px' }} />
        
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Main header row */}
            <div className="flex items-center justify-between py-3 sm:py-4">
              {/* Logo / Business Name */}
              <div className="flex items-center gap-3">
                {logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt={businessName} 
                    className="h-12 sm:h-16 w-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div 
                    className="flex items-center justify-center text-white font-bold text-xl rounded-lg"
                    style={{ 
                      backgroundColor: brandColor,
                      width: '48px',
                      height: '48px'
                    }}
                  >
                    {businessName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <h1 
                    className="text-base sm:text-xl font-bold leading-tight"
                    style={{ color: brandColor }}
                  >
                    {businessName}
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-500">Customer Portal</p>
                </div>
              </div>

              {/* Desktop: User Info & Logout */}
              <div className="hidden sm:flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  Welcome, <span className="font-medium">{customer?.name}</span>
                </span>
                <button
                  onClick={handleLogout}
                  className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <LogOut size={16} />
                  <span>Sign Out</span>
                </button>
              </div>

              {/* Mobile: Menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="sm:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
        
        {/* Desktop Navigation */}
        <div className="hidden sm:block border-t border-gray-100">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex gap-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNavClick(item.path)}
                      className="px-4 py-3 text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 border-b-3"
                      style={active ? { 
                        color: brandColor,
                        borderBottom: `3px solid ${brandColor}`,
                        marginBottom: '-1px'
                      } : {
                        color: '#6b7280',
                        borderBottom: '3px solid transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          e.currentTarget.style.color = brandColor;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          e.currentTarget.style.color = '#6b7280';
                        }
                      }}
                    >
                      <Icon size={18} />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div style={{ height: '1px', backgroundColor: '#e5e7eb' }} />
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-gray-100 bg-white shadow-lg">
            {/* Mobile user info */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
              <p className="text-sm text-gray-600">
                Welcome, <span className="font-medium">{customer?.name}</span>
              </p>
            </div>
            
            {/* Mobile nav items */}
            <nav className="py-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavClick(item.path)}
                    className="w-full px-4 py-3 text-left flex items-center gap-3 transition-colors"
                    style={active ? { 
                      color: brandColor,
                      backgroundColor: `${brandColor}10`
                    } : {
                      color: '#374151'
                    }}
                  >
                    <Icon size={20} />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>
            
            {/* Mobile logout */}
            <div className="border-t border-gray-100 p-4">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut size={18} />
                <span className="font-medium">Sign Out</span>
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Mobile Bottom Navigation */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-20 safe-area-pb">
        <div className="flex justify-around">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <button
                key={item.path}
                onClick={() => handleNavClick(item.path)}
                className="flex-1 flex flex-col items-center py-2 px-1 transition-colors"
                style={active ? { color: brandColor } : { color: '#6b7280' }}
              >
                <Icon size={22} />
                <span className="text-xs mt-1 font-medium truncate">{item.label.replace('Request ', '')}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Main Content - add padding for bottom nav on mobile */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24 sm:pb-8">
        {children}
      </main>

      {/* Footer - hidden on mobile since we have bottom nav */}
      <footer className="hidden sm:block bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            {logoUrl && (
              <img 
                src={logoUrl} 
                alt={businessName} 
                className="h-12 w-auto object-contain mx-auto mb-4 opacity-70"
              />
            )}
            <p className="text-sm text-gray-600 mb-3">
              Questions? Contact <span className="font-medium">{businessName}</span> for assistance.
            </p>
            {(businessPhone || businessEmail) && (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-sm">
                {businessPhone && (
                  <a 
                    href={`tel:${businessPhone.replace(/\D/g, '')}`}
                    className="flex items-center gap-2 hover:underline transition-colors"
                    style={{ color: brandColor }}
                  >
                    <span>📞</span>
                    <span>{businessPhone}</span>
                  </a>
                )}
                {businessEmail && (
                  <a 
                    href={`mailto:${businessEmail}`}
                    className="flex items-center gap-2 hover:underline transition-colors"
                    style={{ color: brandColor }}
                  >
                    <span>✉️</span>
                    <span>{businessEmail}</span>
                  </a>
                )}
              </div>
            )}
            <p className="text-xs text-gray-400 mt-6">
              Powered by <span className="font-medium">SuddyFlow</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PortalLayout;